import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, TrendingUp, Sparkles } from 'lucide-react';
import { stats } from '../data/mock';

const AboutSection = () => {
  return (
    <section className="py-24 bg-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-gold text-sm font-mono uppercase tracking-wider">About Us</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Markaları Dijital Dünyada <span className="text-gold">Güçlendiriyoruz</span>
            </h2>

            <div className="space-y-4 text-gray-400 text-lg leading-relaxed mb-8">
              <p>
                ADK Creative, markaların dijital dünyada daha güçlü görünmesi için modern, sade ve hedef odaklı tasarımlar üretir.
              </p>
              <p>
                Logo, kurumsal kimlik ve sosyal medya tasarımları başta olmak üzere; marka diline uygun, dikkat çekici ve profesyonel işler çıkararak işletmelere değer katmayı hedefleriz.
              </p>
              <p>
                Her projede, markanızın hikayesini görsel dille anlatıyor ve hedef kitlenizle güçlü bir bağ kurmanıza yardımcı oluyoruz.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <button className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-black font-semibold rounded-full hover:bg-gold-light transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-gold/50">
                Portfolyoyu İncele
                <Award className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
              </button>
              <button className="inline-flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 hover:border-gold/30 transition-all duration-300">
                İletişime Geç
              </button>
            </div>
          </motion.div>

          {/* Right: Image & Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden aspect-square">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="ADK Creative Team"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating Stats Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="absolute -bottom-8 -left-8 bg-black/80 backdrop-blur-xl border border-gold/20 rounded-2xl p-6 shadow-2xl"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gold/20 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">50+</div>
                  <div className="text-sm text-gray-400">Tamamlanan Proje</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="absolute -top-8 -right-8 bg-black/80 backdrop-blur-xl border border-gold/20 rounded-2xl p-6 shadow-2xl"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gold/20 rounded-xl">
                  <Users className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">30+</div>
                  <div className="text-sm text-gray-400">Mutlu Müşteri</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 p-8 bg-dark-grey rounded-2xl border border-white/5"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-gold mb-2">
                {stat.value}
              </div>
              <div className="text-gray-400 text-sm">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
